import { HStack, VStack } from '@cbhq/cds-web/layout';
import { TextHeadline, TextTitle1, TextTitle3, TextLabel2 } from '@cbhq/cds-web/typography';
import { memo, useMemo } from 'react';
import { useExploreData } from '@/components/Explore/hooks/useExploreData';
import { DisplayType, format } from '@/utils/numberFormatter';

const SUMMARY_CARD_TITLE = 'Pools Overview';
const SUMMARY_ITEMS_GAP = 3;

type SummaryItem = {
  label: string;
  value: string;
  change?: number;
};

function ExploreSummaryItems() {
  const { summary } = useExploreData();

  const summaryItems: SummaryItem[] = useMemo(
    () => [
      {
        label: 'Total volume',
        value: format({ value: summary.totalVolume, type: DisplayType.FiatShort }),
        change: summary.totalVolumeChange,
      },
      {
        label: 'Volume 24H',
        value: format({ value: summary.volume24h, type: DisplayType.FiatShort }),
      },
      {
        label: 'TVL',
        value: format({ value: summary.tvl, type: DisplayType.FiatShort }),
        change: summary.tvlChange,
      },
    ],
    [summary],
  );

  return (
    <>
      {summaryItems.map((item) => {
        return (
          <VStack key={item.label} gap={0.5} flexGrow={1}>
            <TextHeadline as="span" noWrap color="fgMuted">
              {item.label}
            </TextHeadline>
            <HStack gap={1} alignItems="center">
              <TextTitle1 as="span">{item.value}</TextTitle1>
              {item.change !== undefined && (
                <TextLabel2 as="span" color="fgPositive">
                  {item.change > 0 ? '↑' : '↓'}
                  {item.change}%
                </TextLabel2>
              )}
            </HStack>
          </VStack>
        );
      })}
    </>
  );
}

const ExploreSummaryContent = memo(function ExploreSummaryContent() {
  return (
    <VStack gap={3}>
      {/* Title */}
      <TextTitle3 as="h1" testID="explore-summary-title">
        {SUMMARY_CARD_TITLE}
      </TextTitle3>

      {/* Summary Items - always vertical */}
      <VStack gap={SUMMARY_ITEMS_GAP}>
        <ExploreSummaryItems />
      </VStack>
    </VStack>
  );
});

export const ExploreSummary = memo(function ExploreSummary() {
  return (
    <VStack background="bg" borderRadius={300} padding={3}>
      <ExploreSummaryContent />
    </VStack>
  );
});
